import { Field, FieldHint, Input, Label, cn } from "@breenova/ui";
import { useId, useMemo, useRef, useState } from "react";

import { useAvailableRingNumbers } from "@/lib/birds";

export interface RingNumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Season whose rings to offer. Defaults to the current year. */
  year?: number | undefined;
  disabled?: boolean | undefined;
}

/**
 * Ring number entry, backed by the rings the breeder actually has left.
 *
 * A combobox rather than a plain select: a bird may wear a ring from a batch that was
 * never recorded here, or an old ring reused from stock, so free text has to stay
 * possible. The suggestions come from the unused serials of the recorded batches, which
 * is the case that matters — ringing a nest of chicks in sequence, one-handed.
 *
 * Built on a native input with an ARIA combobox pattern rather than a `datalist`, which
 * renders inconsistently and cannot be styled to the design system at all.
 */
export function RingNumberField({ value, onChange, year, disabled }: RingNumberFieldProps) {
  const inputId = useId();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const season = year ?? new Date().getFullYear();
  const available = useAvailableRingNumbers(season, !disabled);

  const suggestions = useMemo(() => {
    const rings = available.data?.rings ?? [];
    const typed = value.trim().toUpperCase();

    // Filtered as the breeder types, so a half-typed serial narrows the strip.
    const matches = typed
      ? rings.filter((ring) => ring.ringNumber.toUpperCase().includes(typed))
      : rings;

    return matches.slice(0, 8);
  }, [available.data, value]);

  function choose(ringNumber: string): void {
    onChange(ringNumber);
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (!open || suggestions.length === 0) {
      if (event.key === "ArrowDown") {
        setOpen(true);
      }

      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter") {
      // Only when a suggestion is actually highlighted, so Enter still submits the form
      // for a breeder typing a ring number the batches do not know about.
      const choice = suggestions[highlighted];
      if (choice) {
        event.preventDefault();
        choose(choice.ringNumber);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <Field className="relative">
      <Label htmlFor={inputId}>Ring number</Label>

      <Input
        id={inputId}
        role="combobox"
        aria-expanded={open && suggestions.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
        data-numeric
        disabled={disabled}
        placeholder="NL-1234-25-007"
        // The one field on the wizard's first step that carries a bird's identity, so
        // it is set a step larger and heavier than every other input.
        className="text-lead font-bold"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setHighlighted(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Deferred: a click on a suggestion fires blur first, and closing immediately
          // would unmount the option before its click lands.
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
      />

      {open && suggestions.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Unused rings"
          className={cn(
            "absolute inset-x-0 top-full z-20 mt-xs max-h-64 overflow-y-auto",
            "rounded-lg bg-surface-container-lowest shadow-raised",
          )}
        >
          {suggestions.map((ring, index) => (
            <li key={ring.ringNumber}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlighted}
                onMouseEnter={() => setHighlighted(index)}
                onMouseDown={() => clearTimeout(blurTimer.current)}
                onClick={() => choose(ring.ringNumber)}
                className={cn(
                  "flex min-h-touch w-full items-center justify-between gap-md px-md text-left",
                  "text-data text-on-surface transition-colors",
                  index === highlighted ? "bg-surface-container" : "",
                )}
                data-numeric
              >
                <span>{ring.ringNumber}</span>
                <span className="text-label-caps uppercase text-on-surface-variant">
                  {ring.ringSize}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <FieldHint>
        {available.data && available.data.rings.length > 0
          ? `${available.data.rings.length} unused rings from your ${season} batches.`
          : "No ring batches recorded for this season — type the number as it is stamped."}
      </FieldHint>
    </Field>
  );
}
